import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Circle, Loader2, AlertTriangle, Info, ShieldCheck, MessageCircle, Trash, Inbox as InboxIcon } from "lucide-react";

// Which wizard step the user should restart from, per error code.
export const RETRY_STEP = {
  popup_blocked: 1, cancelled: 2, session_expired: 2, no_code: 2, meta_error: 2, sdk_failed: 1,
  permissions_denied: 7, number_in_use: 6, coexistence_unavailable: 5, platform_not_ready: 1, callback_failed: 9,
};

// Maps a launcher/API error into a human-recoverable code. Never exposes tokens.
export function classifyError(e) {
  if (e?.code && RETRY_STEP[e.code]) return e.code;
  const status = e?.response?.status;
  const msg = String(e?.response?.data?.detail || e?.message || "").toLowerCase();
  if (status === 503 || msg.includes("meta platform")) return "platform_not_ready";
  if (msg.includes("popup")) return "popup_blocked";
  if (msg.includes("annullat") || msg.includes("cancel")) return "cancelled";
  if (msg.includes("permess") || msg.includes("permission") || msg.includes("scope")) return "permissions_denied";
  if (msg.includes("già") || msg.includes("already") || msg.includes("in use") || msg.includes("associat")) return "number_in_use";
  if (msg.includes("coexist")) return "coexistence_unavailable";
  if (msg.includes("codice") || msg.includes("code") || msg.includes("sessione") || msg.includes("session") || msg.includes("expired") || msg.includes("scadut")) return "session_expired";
  return "callback_failed";
}

const STEP_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// Derives each step's state from the real connection status + launcher phase (no cross-origin reads).
function stepStates({ status, phase, errorCode, lastInbound }) {
  if (status === "connected") return STEP_IDS.map((n) => (n < 11 ? "done" : lastInbound ? "done" : "current"));
  if (errorCode) { const r = RETRY_STEP[errorCode] || 1; return STEP_IDS.map((n) => (n < r ? "done" : n === r ? "error" : "pending")); }
  if (phase === "popup") return STEP_IDS.map((n) => (n === 1 ? "done" : n <= 8 ? "current" : "pending"));
  if (phase === "meta_finished" || phase === "code") return STEP_IDS.map((n) => (n <= 8 ? "done" : n === 9 ? "current" : "pending"));
  if (phase === "server") return STEP_IDS.map((n) => (n <= 9 ? "done" : n === 10 ? "current" : "pending"));
  return STEP_IDS.map((n) => (n === 1 ? "current" : "pending"));
}

function StepIcon({ s }) {
  if (s === "done") return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />;
  if (s === "current") return <Loader2 className="w-4 h-4 text-sky-600 animate-spin shrink-0" />;
  if (s === "error") return <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />;
  return <Circle className="w-4 h-4 text-slate-300 shrink-0" />;
}

function PreCheck() {
  const { t } = useTranslation();
  const [checked, setChecked] = useState({});
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-2" data-testid="wa-guide-precheck">
      <div className="font-semibold text-[#0F172A] text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-600" /> {t("wa_guide.precheck_title")}</div>
      {[1, 2, 3, 4, 5].map((n) => (
        <label key={n} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" className="mt-0.5 accent-sky-600" data-testid={`wa-precheck-${n}`} checked={!!checked[n]} onChange={(e) => setChecked({ ...checked, [n]: e.target.checked })} />
          <span>{t(`wa_guide.pre_${n}`)}</span>
        </label>
      ))}
    </div>
  );
}

function ConnectedPanel({ guide, admin, onTest, onDisconnect }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [ack, setAck] = useState(false);
  const fmt = (v) => (v ? new Date(v).toLocaleString(i18n.language) : t("wa_guide.never"));
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="wa-connected-box">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> {t("wa_guide.connected_title")}</div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div><dt className="text-xs text-slate-500">{t("wa_guide.number")}</dt><dd className="font-mono font-semibold text-[#0F172A]" data-testid="wa-connected-number">{guide.display_phone_number || "—"}</dd></div>
        <div><dt className="text-xs text-slate-500">{t("wa_guide.account")}</dt><dd className="font-semibold text-[#0F172A]" data-testid="wa-connected-name">{guide.verified_name || "—"}</dd></div>
        <div><dt className="text-xs text-slate-500">{t("wa_guide.last_webhook")}</dt><dd data-testid="wa-last-webhook">{fmt(guide.last_webhook_at)}</dd></div>
        <div><dt className="text-xs text-slate-500">{t("wa_guide.ai_state")}</dt><dd data-testid="wa-ai-state" className={guide.ai_active ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{guide.ai_active ? t("wa_guide.ai_on") : t("wa_guide.ai_off")}</dd></div>
      </dl>
      {guide.coexistence && <div className="text-xs text-emerald-800 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> {t("wa_guide.coexistence_on")}</div>}
      <div className="rounded-lg bg-white border border-emerald-100 p-3 text-sm" data-testid="wa-test-message-box">
        <div className="font-semibold text-[#0F172A] flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-600" /> {t("wa_guide.test_msg_title")}</div>
        <p className="text-slate-600 mt-1">{t("wa_guide.test_msg_body")}</p>
      </div>
      {admin && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={onTest} variant="outline" data-testid="wa-test-btn">{t("wa_guide.test_conn_btn")}</Button>
          <Button onClick={() => nav("/inbox")} variant="outline" className="gap-1.5" data-testid="wa-open-inbox-btn"><InboxIcon className="w-4 h-4" /> {t("wa_guide.open_inbox_btn")}</Button>
          <Button onClick={() => { setAck(false); setConfirm(true); }} variant="outline" className="border-red-300 text-red-600 gap-1.5" data-testid="wa-disconnect-btn"><Trash className="w-4 h-4" /> {t("wa_guide.disconnect_btn")}</Button>
        </div>
      )}
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="wa-disconnect-dialog">
          <DialogHeader><DialogTitle>{t("wa_guide.disconnect_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("wa_guide.disconnect_body")}</p>
          <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-0.5 accent-red-600" data-testid="wa-disconnect-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} /> {t("wa_guide.disconnect_confirm_label")}</label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} data-testid="wa-disconnect-cancel">{t("wa_guide.cancel")}</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={!ack} onClick={() => { setConfirm(false); onDisconnect(); }} data-testid="wa-disconnect-confirm">{t("wa_guide.disconnect_confirm_btn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function WhatsAppGuide({ admin, guide, phase, errorCode, connecting, platformReady, onConnect, onConnectNew, onTest, onDisconnect }) {
  const { t } = useTranslation();
  const connected = guide?.status === "connected";
  const [open, setOpen] = useState(!connected);
  const states = stepStates({ status: guide?.status, phase, errorCode, lastInbound: guide?.last_inbound_at });
  const retryStep = errorCode ? RETRY_STEP[errorCode] || 1 : null;

  return (
    <div className="space-y-4" data-testid="wa-guide">
      {connected && <ConnectedPanel guide={guide} admin={admin} onTest={onTest} onDisconnect={onDisconnect} />}
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-sm font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2" data-testid="wa-guide-toggle">
        {open ? t("wa_guide.close_btn") : t("wa_guide.open_btn")}
      </button>

      {open && (
        <div className="space-y-4" data-testid="wa-guide-body">
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-1.5" data-testid="wa-guide-intro">
            <div className="font-semibold text-[#0F172A] text-sm flex items-center gap-2"><Info className="w-4 h-4 text-sky-600" /> {t("wa_guide.intro_title")}</div>
            {[1, 2, 3, 4].map((n) => <p key={n} className="text-sm text-slate-700 flex gap-2"><span className="text-sky-600">•</span><span>{t(`wa_guide.intro_${n}`)}</span></p>)}
          </div>
          {!connected && <PreCheck />}

          <div className="rounded-xl border border-slate-200 p-4" data-testid="wa-guide-steps">
            <div className="font-semibold text-[#0F172A] text-sm mb-2">{t("wa_guide.steps_title")}</div>
            <ol className="space-y-1.5">
              {STEP_IDS.map((n, i) => (
                <li key={n} className={`flex items-start gap-2 text-sm rounded-lg px-2 py-1 ${states[i] === "current" ? "bg-sky-50" : states[i] === "error" ? "bg-red-50" : ""}`} data-testid={`wa-step-${n}`} data-state={states[i]}>
                  <StepIcon s={states[i]} />
                  <span className={states[i] === "done" ? "text-slate-400" : "text-slate-700"}><span className="font-semibold text-slate-500 mr-1">{n}.</span>{t(`wa_guide.step_${n}`)}</span>
                </li>
              ))}
            </ol>
          </div>

          {phase === "popup" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" role="status" data-testid="wa-meta-now">
              <div className="font-semibold text-amber-800 text-sm">{t("wa_guide.meta_now_title")}</div>
              <p className="text-sm text-amber-800 mt-1">{t("wa_guide.meta_now_body")}</p>
              <p className="text-xs text-amber-700 mt-1">{t("wa_guide.meta_now_hint")}</p>
            </div>
          )}
          {(phase === "meta_finished" || phase === "code" || phase === "server") && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 flex items-center gap-2" role="status" data-testid="wa-server-now"><Loader2 className="w-4 h-4 animate-spin" /> {t("wa_guide.server_now")}</div>
          )}
          {errorCode && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2" role="alert" data-testid="wa-guide-error" data-code={errorCode}>
              <div className="font-semibold text-red-700 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {t(`wa_guide.err_${errorCode}`)}</div>
              {admin && errorCode !== "platform_not_ready" && (
                <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => onConnect(true)} disabled={connecting} data-testid="wa-retry-btn">
                  {t("wa_guide.retry_from", { n: retryStep })}
                </Button>
              )}
            </div>
          )}

          {!connected && admin && (
            <div className="space-y-2">
              {!platformReady && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="wa-platform-not-ready">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {t("wa_guide.err_platform_not_ready")}
                </div>
              )}
              <Button onClick={() => onConnect(true)} disabled={!platformReady || connecting} className="bg-emerald-600 hover:bg-emerald-700 gap-2 h-11" data-testid="wa-connect-btn">
                <MessageCircle className="w-4 h-4" /> {connecting ? t("wa_guide.connecting_btn") : t("wa_guide.connect_btn")}
              </Button>
              <p className="text-xs text-slate-500">{t("wa_guide.keep_number_hint")}</p>
              <button type="button" onClick={() => onConnectNew()} disabled={!platformReady || connecting} className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 disabled:opacity-50" data-testid="wa-connect-new-number-btn">
                {t("wa_guide.new_number_link")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
